<script setup>
import moment from "moment/moment";
import {computed, ref, watch, onMounted, onUnmounted} from "vue";
import {useSorting} from "@/composables/useSorting";
import WorksProject from "../works/WorksProject.vue";
import forestreeHelpers from "@/helpers/forestreeHelpers";
import AssignWork from "@/components/AssignWork.vue";
import BulkAssign from "@/components/home/dashboard/works/BulkAssign.vue";
import BulkCompleteWorks from "@/components/home/dashboard/works/BulkCompleteWorks.vue";
import useMapImagery from "@/components/maps/shared/composables/useMapImagery.js";
import useMapFilter from "@/composables/useMapFilter";
import AddressSearch from "@/components/maps/shared/AddressSearch.vue";
import useDashboardMapFilter from "@/components/home/dashboard/composables/useDashboardMapFilter";

const props = defineProps({
    works: {},
    suburbs: {},
    equipment: {},
    users: {},
    teams: {},
    tasks: {},
    permissions: {},
    tags: {},
    refreshData: {
        type: Function,
        required: true
    },
    zones: {},
    defaults: {}
});

const page = ref(1);
const recentWorks = ref([]);
const selectedUser = ref([]);
const selectedTask = ref([]);
const selectedEquip = ref([]);
const selectedSuburb = ref([]);
const selectedPriority = ref([]);
const selectedRisk = ref([]);
const selectedStormEvent = ref(null);
const selectedCreator = ref([]);
const selectedTags = ref([]);
const showMore = ref(false);
const showLimit = ref(50);
const filteredLength = ref(null);
const unassignedCount = ref(null);
const projectsCount = ref(null);
const assignTypeId = ref(null);
const showAssign = ref(false);
const crms = ref(null);
const showWorkOrder = ref(false);
const selectedWorkOrderURL = ref(null);
const today = ref(moment().format("dddd Do MMM YYYY"));
const showMap = ref(false);
const forMap = ref(null);
const openActions = ref(false);
const showBulkAssign = ref(false);
const showWorksProject = ref(false);
const showBulkComplete = ref(false);
const actions = ref(null);
const openArea = ref(false)

const {sortBy, sortAsc, sortArray} = useSorting('created_at');
const {
    map,
    osm,
    imageService,
    aerialGroup,
    labels,
    satellite,
    aerial,
    selectedFeatures,
    createMap,
    addTreesGL,
    enablePolygonDraw,
    restart,
    polygonSource,
    polygonWkt,
    addFeatureToMap
} = useDashboardMapFilter();

const {baseMapSwitch, addAdditionalLayers} = useMapImagery({
    map,
    osmLayer: osm,
    imageService,
    aerialGroup,
    labelLayer: labels,
    satelliteLayer: satellite,
    aerial
});

const initMap = () => {
    createMap(true);
    addAdditionalLayers();
    document.addEventListener("click", close);
    addTreesGL(forMap.value);
};

const close = (e) => {
    if (actions.value && !actions.value.contains(e.target)) {
        //openActions.value = false;
    }
};

onMounted(() => {
    if (showMap.value) {
        initMap();
    }
});

watch(showMap, (newValue) => {
    if (newValue) {
        initMap();
    }
});

onUnmounted(() => {
    document.removeEventListener("click", close);
});

const isOverdue = (date) => {
    if (moment(date).isValid()) {
        let diff = moment(date).diff(moment(), 'days');
        return diff < 0;

    } else {
        return false;
    }
}
const bulkUpdated = () => {
    props.refreshData();
    showBulkAssign.value = false;
};

const setBulkAssign = (updatedBulkUpdated) => {
    showBulkAssign.value = updatedBulkUpdated;
};
const showWO = (woUrl) => {
    selectedWorkOrderURL.value = woUrl;
    showWorkOrder.value = true;
};

const toggleToArray = (userId) => {
    if (selectedUser.value.includes(userId)) {
        selectedUser.value.splice(selectedUser.value.indexOf(userId), 1);
    } else {
        selectedUser.value.push(userId);
    }
};

const openActionsMenu = () => {
    openActions.value = !openActions.value;
    if (openActions.value) {
        openArea.value = false;
    }
};
const toggleEquip = (e) => {
    if (!selectedEquip.value.includes(e)) {
        selectedEquip.value.push(e);
    } else {
        selectedEquip.value.splice(selectedEquip.value.indexOf(e), 1);
    }
};
const toggleTasks = (e) => {
    if (!selectedTask.value.includes(e)) {
        selectedTask.value.push(e);
    } else {
        selectedTask.value.splice(selectedTask.value.indexOf(e), 1);
    }
};

const filteredAssignee = computed(() => {
    let filteredList = props.works;

    if (selectedUser.value.length) {
        filteredList = filteredList.filter((work) => {
            if (selectedUser.value.includes(-99)) {
                if (selectedUser.value.includes(-98)) {
                    return (
                        work.assignments.length === 0 ||
                        work.projects.length > 0 ||
                        work.assignments.filter((user) => selectedUser.value.includes(user.user_id)).length
                    );
                } else {
                    return (
                        (work.assignments.length === 0 && work.projects.length === 0) ||
                        work.assignments.filter((user) => selectedUser.value.includes(user.user_id)).length
                    );
                }
            } else if (selectedUser.value.includes(-98)) {
                return work.projects.length > 0 || work.assignments.filter((user) => selectedUser.value.includes(user.user_id)).length;
            } else {
                return work.assignments.filter((user) => selectedUser.value.includes(user.user_id)).length;
            }
        });
    }

    if (selectedEquip.value.length > 0) {
        filteredList = filteredList.filter((work) => {
            if (work.equipment) {
                return work.equipment.filter((e) => selectedEquip.value.includes(e.name)).length;
            }
        });
    }

    if (selectedSuburb.value.length > 0) {
        const lowercaseSuburbs = selectedSuburb.value.map((s) => s.toLowerCase());
        filteredList = filteredList.filter((work) => {
            if (work.city) {
                return lowercaseSuburbs.includes(work.city.toLowerCase());
            }
        });
    }

    if (selectedTask.value.length > 0) {
        filteredList = filteredList.filter((work) => {
            if (work.inspection) {
                if (work.inspection.works_as_list) {
                    return work.inspection.works_as_list.filter((t) => selectedTask.value.includes(t)).length;
                }
            }
        });
    }

    if (selectedPriority.value.length > 0) {
        filteredList = filteredList.filter((work) => {
            if (work.inspection) {
                return selectedPriority.value.includes(work.inspection.priority);
            }
        });
    }

    if (selectedRisk.value.length > 0) {
        filteredList = filteredList.filter((work) => {
            return selectedRisk.value.includes(work.trees_risk_id);
        });
    }

    if (selectedStormEvent.value) {
        let se = false;
        if (selectedStormEvent.value === "Yes") {
            se = true;
        }
        filteredList = filteredList.filter((work) => {
            return se === work.storm_event;
        });
    }

    if (selectedCreator.value.length > 0) {
        filteredList = filteredList.filter((work) => {
            if (work.user_id) {
                return selectedCreator.value.includes(work.user_id);
            }
        });
    }

    if (selectedTags.value.length > 0) {
        filteredList = filteredList.filter((work) => {
            if (work.tree) {
                if (work.tree.tags) {
                    return work.tree.tags.filter((t) => selectedTags.value.includes(t.id)).length;
                }
            }
        });
    }

    filteredLength.value = filteredList.length;
    forMap.value = filteredList;

    return sortArray(filteredList, sortBy.value).slice(0, showLimit.value);
});

watch(filteredAssignee, () => {
    addTreesGL(forMap.value);
})

const userTotals = computed(() => {
    if (props.users) {
        for (let i = 0; i < props.users.active_users.length; i++) {
            const all = props.works.filter((work) => {
                return work.assignments.filter((user) => user.user_id === props.users.active_users[i].id).length;
            });

            if (all.length) {
                props.users.active_users[i].jobs = all.length;
            }

            const unassigned = props.works.filter((work) => {
                return work.assignments.length === 0 && work.projects.length === 0;
            });

            if (unassigned.length) {
                unassignedCount.value = unassigned.length;
            } else {
                unassignedCount.value = null;
            }

            const projects = props.works.filter((work) => {
                return work.projects.length > 0;
            });

            if (projects.length) {
                projectsCount.value = projects.length;
            } else {
                projectsCount.value = null;
            }
        }
        return props.users.active_users;
    }
});

//region Choose Area/zones
// props - zones
const isAreaMenuOpen = ref(false);
const toggleShowAreaMenu = () => {
    isAreaMenuOpen.value = !isAreaMenuOpen.value;
};

const clearArea = () => {
    if (polygonSource.value) {
        restart();
    }
};

const handleSelectArea = (area) => {
    addFeatureToMap(area, true);
    isAreaMenuOpen.value = false;
}

//endregion


</script>

<template>
    <div v-if="page === 1">
        <div v-if="users && users.active_users.length > 0"
             class="py-4 flex flex-no-wrap lg:flex-wrap overflow-x-auto items-center">
            <p class="w-full whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-2">Jobs by User</p>
            <template v-for="user in userTotals">
                <span
                    v-if="user.jobs"
                    :key="user.id"
                    @click.prevent="toggleToArray(user.id)"
                    :class="selectedUser.includes(user.id) ? 'bg-green-200 hover:bg-green-300' : 'bg-slate-100 hover:bg-slate-200'"
                    class="flex rounded-lg text-slate-500 text-center w-auto mr-3 mt-1 p-3 cursor-pointer hover:text-blue-600"
                >
                    <avatar
                        :title="user.name"
                        class="cursor-pointer"
                        :name="user.name.replace(/([0-9])/g, ' $1').trim()"
                        :background="forestreeHelpers.getAvatarColor(user.name.replace(/([0-9])/g, ' $1').trim())"
                        color="#fff"
                        :size="32"
                        :customAvatarStyle="{
                            fontFamily: 'Arial',
                            fontSize: '12px',
                            'font-weight': 100
                        }"
                    >
                    </avatar>
                    <p class="font-semibold px-2 items-center text-lg">
                        {{ user.jobs }}
                    </p>
                </span>
            </template>

            <div class="border-l border-slate-300 pr-3 h-16"></div>
            <span
                v-if="projectsCount > 0"
                @click.prevent="toggleToArray(-98)"
                :class="selectedUser.includes(-98) ? 'bg-orange-200 hover:bg-orange-300' : 'border border-orange-300 hover:bg-orange-100'"
                class="flex rounded-lg text-slate-500 text-center w-auto mr-3 mt-1 p-3 cursor-pointer hover:text-blue-600"
            >
                <avatar
                    title="Projects"
                    class="cursor-pointer"
                    name="Projects"
                    background="#ed8936"
                    color="#FFFFFF"
                    :size="32"
                    :customAvatarStyle="{
                        fontFamily: 'Arial',
                        fontSize: '12px',
                        'font-weight': 100
                    }"
                >
                </avatar>
                <p class="font-semibold px-2 items-center text-lg">
                    {{ projectsCount }}
                </p>
            </span>
            <span
                v-if="unassignedCount > 0"
                @click.prevent="toggleToArray(-99)"
                :class="selectedUser.includes(-99) ? 'bg-green-200 hover:bg-green-300' : 'border border-slate-300 hover:bg-slate-100'"
                class="flex rounded-lg text-slate-500 text-center w-auto mr-3 mt-1 p-3 cursor-pointer hover:text-blue-600"
            >
                <avatar
                    title="Unassigned"
                    class="cursor-pointer"
                    name="Unassigned"
                    background="#718096"
                    color="#FFFFFF"
                    :size="32"
                    :customAvatarStyle="{
                        fontFamily: 'Arial',
                        fontSize: '12px',
                        'font-weight': 100
                    }"
                >
                </avatar>
                <p class="font-semibold px-2 items-center text-lg">
                    {{ unassignedCount }}
                </p>
            </span>
        </div>

        <div class="w-full flex flex-wrap lg:flex-no-wrap pb-1">
            <div v-if="suburbs.length" class="w-full lg:w-1/4 pr-4">
                <div class="flex justify-end my-3">
                    <div class="w-full items-center">
                        <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-1">Priority</p>

                        <v-select
                            class="style-chooser"
                            multiple
                            :searchable="false"
                            v-model="selectedPriority"
                            :options="[
                                { type: 'Low', id: 1 },
                                { type: 'Medium', id: 2 },
                                { type: 'High', id: 3 },
                                { type: 'Extreme', id: 4 },
                            ]"
                            label="type"
                            :reduce="(type) => type.id"
                        />
                    </div>
                </div>
            </div>
            <div v-if="suburbs.length" class="w-full lg:w-1/4 pr-4">
                <div class="flex justify-end my-3">
                    <div class="w-full items-center">
                        <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-1">Suburb</p>

                        <v-select class="style-chooser" multiple :searchable="false" v-model="selectedSuburb"
                                  :options="suburbs" label="suburb"/>
                    </div>
                </div>
            </div>
            <div v-if="tasks && tasks.length" class="w-full lg:w-1/4 pr-4">
                <div class="flex justify-end my-3">
                    <div class="w-full items-center">
                        <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-1">Work Task</p>
                        <v-select
                            class="style-chooser"
                            multiple
                            :searchable="false"
                            v-model="selectedTask"
                            :options="tasks"
                            label="task"
                            :reduce="(task) => task.id"
                        />
                    </div>
                </div>
            </div>
            <div class="w-full lg:w-1/4 pr-4">
                <div class="flex justify-end my-3">
                    <div class="w-full items-center">
                        <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-1">Equipment
                            Req.</p>
                        <v-select class="style-chooser" multiple :searchable="false" v-model="selectedEquip"
                                  :options="equipment" label="equipment"/>
                    </div>
                </div>
            </div>
        </div>

        <transition name="slide-down">
            <div v-if="showMore">
                <div class="w-full flex flex-wrap lg:flex-no-wrap justify-center pb-3">
                    <div class="w-full lg:w-1/4 pr-4">
                        <div class="flex justify-end my-3">
                            <div class="w-full items-center">
                                <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-1">Tree
                                    Risk</p>

                                <v-select
                                    class="style-chooser"
                                    multiple
                                    :searchable="false"
                                    v-model="selectedRisk"
                                    :options="[
                                        { type: 'Low', id: 1 },
                                        { type: 'Moderate', id: 2 },
                                        { type: 'High', id: 3 },
                                        { type: 'Extreme', id: 4 },
                                        { type: 'Unassessed Risk', id: 0 },
                                    ]"
                                    label="type"
                                    :reduce="(type) => type.id"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="w-full lg:w-1/4 pr-4">
                        <div class="flex justify-end my-3">
                            <div class="w-full items-center">
                                <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-1">Storm
                                    Event</p>

                                <v-select
                                    class="style-chooser"
                                    :searchable="false"
                                    v-model="selectedStormEvent"
                                    :options="[
                                        { type: 'Yes', value: 'Yes' },
                                        { type: 'No', value: 'No' },
                                    ]"
                                    label="type"
                                    :reduce="(type) => type.value"
                                />
                            </div>
                        </div>
                    </div>

                    <div v-if="users && users.active_users.length" class="w-full lg:w-1/4 pr-4">
                        <div class="flex justify-end my-3">
                            <div class="w-full items-center">
                                <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-1">Created
                                    By</p>

                                <v-select
                                    class="style-chooser"
                                    multiple
                                    :searchable="false"
                                    v-model="selectedCreator"
                                    :options="users.active_users"
                                    label="name"
                                    :reduce="(name) => name.id"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="w-full lg:w-1/4 pr-4">
                        <div class="flex justify-end my-3">
                            <div class="w-full items-center">
                                <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-1">
                                    Tags</p>

                                <v-select
                                    class="style-chooser"
                                    multiple
                                    :searchable="false"
                                    v-model="selectedTags"
                                    :options="tags"
                                    label="name"
                                    :reduce="(name) => name.id"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <div class="w-full flex flex-wrap lg:flex-no-wrap justify-center pb-3">
            <div
                @click="showMore = !showMore"
                class="text-sm w-auto font-medium cursor-pointer text-md text-blue-700 hover:text-blue-500 bg-slate-100 p-2 hover:bg-slate-200 rounded-lg"
            >
                + Show {{ showMore ? "Less" : "More" }} Filters
            </div>
        </div>

        <div class="w-full text-slate-600 flex justify-end items-end">
            <div class="flex flex-col items-end md:flex-row md:items-center">
                <div v-if="selectedFeatures && selectedFeatures.length > 0"
                     class="pr-2 border-r border-slate-300 flex items-center">
                    <div class="pr-2">
                        <strong>{{ selectedFeatures.length }}</strong> Works Selected
                    </div>
                    <div ref="actions">
                        <button @click.prevent="openActionsMenu()"
                                class="mr-2 bg-slate-100 hover:bg-slate-200 font-semibold text-slate-600 p-3 rounded-lg">
                            Actions
                        </button>

                        <div v-if="openActions" class="relative h-0 left-0" role="menu" aria-orientation="vertical"
                             aria-labelledby="menu-button" tabindex="-1">
                            <div
                                class="mt-1 py-1 z-20 absolute rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none w-56"
                                role="none "
                            >
                                <div
                                    @click="
                                        showBulkAssign = true;
                                        openActions = false;
                                    "
                                    class="cursor-pointer hover:bg-slate-100 text-slate-700 block px-4 py-2 text-sm"
                                    role="menuitem"
                                    tabindex="-1"
                                    id="menu-item-0"
                                >
                                    Assign Selected Works
                                </div>
                                <div
                                    @click="
                                        showWorksProject = true;
                                        openActions = false;
                                    "
                                    class="cursor-pointer hover:bg-slate-100 text-slate-700 block px-4 py-2 text-sm"
                                    role="menuitem"
                                    tabindex="-1"
                                    id="menu-item-1"
                                >
                                    Create Project from Selection
                                </div>
                                <div
                                    v-if="permissions.admin_access"
                                    @click="
                                        showBulkComplete = true;
                                        openActions = false;
                                    "
                                    class="cursor-pointer hover:bg-slate-100 text-slate-700 block px-4 py-2 text-sm"
                                    role="menuitem"
                                    tabindex="-1"
                                    id="menu-item-1"
                                >
                                    Bulk Complete Works
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="showMap" class="py-4 px-4 border-r border-slate-300 flex flex-row">
                    <button @click.prevent="enablePolygonDraw()"
                            class="mr-2 bg-slate-100 hover:bg-slate-200 font-semibold text-slate-600 p-3 rounded-lg">
                        Draw Area
                    </button>

                    <button
                        v-if="zones && zones.length"
                        ref="actions"
                        @click.prevent="toggleShowAreaMenu()"
                        class="mr-2 bg-slate-100 hover:bg-slate-200 font-semibold text-slate-600 p-3 rounded-lg"
                    >
                        Choose Area
                    </button>
                    <div
                        v-if="isAreaMenuOpen"
                        class="w-0 relative h-0 right-0"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="menu-button"
                        tabindex="-1"
                    >
                        <div
                            class="mt-1 py-1 z-20 absolute  right-0 top-10 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none w-56"
                        >
                            <div
                                v-for="z in zones"
                                :key="z.id"
                                @click="handleSelectArea(z.polygon)"
                                class="cursor-pointer hover:bg-slate-100 text-slate-700 block px-4 py-2 text-sm"
                                role="menuitem"
                                tabindex="-1"
                                id="menu-item-0"
                            >
                                {{ z.name }}
                            </div>
                        </div>
                    </div>
                    <button
                        @click.prevent="clearArea()"
                        class="px-4 py-3 bg-slate-100 rounded-lg font-medium cursor-pointer text-md text-blue-700 hover:text-blue-500"
                    >
                        Remove Area
                    </button>
                </div>
                <div class="py-4 pl-4">
                    <button
                        @click.prevent="showMap = !showMap"
                        class="mr-4 font-medium cursor-pointer text-md text-blue-700 hover:text-blue-500 bg-slate-100 p-3 hover:bg-slate-200 rounded-lg"
                    >
                        Toggle Map
                    </button>
                </div>
            </div>
        </div>
        <div class="text-slate-500 pb-2">
            Showing <b>{{ filteredAssignee.length }}</b> works of
            <b>{{ filteredLength }}</b>
            <span v-if="works.length > filteredLength">(filtered)</span>
        </div>

        <transition name="slide-down">
            <div :class="showMap ? 'visible map-height' : 'invisible h-0'">
                <div id="map" class="pb-6" style="height: 70vh">
                    <address-search class="relative w-full" :toggleMap="baseMapSwitch" :map="map"></address-search>

                    <div id="popup" class="ol-popup">
                        <a href="#" id="popup-closer" class="ol-popup-closer"></a>
                        <div id="popup-content" class="text-slate-600"></div>
                    </div>
                </div>
            </div>
        </transition>

        <div class="w-full flex flex-col">
            <div class="-my-2 overflow-x-auto sm:-mx-1 lg:-mx-8">
                <div class="py-2 align-middle inline-block w-full">
                    <div class="overflow-x-auto border-b border-slate-200">
                        <div class="min-w-full divide-y divide-slate-200">
                            <div class="flex w-full bg-slate-100 items-center">
                                <div
                                    scope="col"
                                    class="min-w-1-3 lg:w-1/6 bg-slate-100 px-2 lg:pl-6 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
                                >
                                    <div class="flex items-center">
                                        <span class="h-3 w-3 rounded-full ring-2 ring-white bg-slate-400"
                                              title="Works Priority"></span>
                                        <div class="pl-1">Priority</div>
                                    </div>
                                    <div class="flex items-center">
                                        <span class="h-3 w-3 rounded-full border-2 border-slate-400"
                                              title="Tree Risk"></span>
                                        <div class="pl-1">Tree Risk</div>
                                    </div>
                                </div>
                                <div
                                    @click.prevent="
                                        sortBy = 'created_at';
                                        sortAsc = !sortAsc;
                                    "
                                    :class="sortBy === 'created_at' ? 'font-semibold text-blue-600' : 'font-medium text-slate-600'"
                                    scope="col"
                                    class="min-w-1-3 lg:w-1/6 bg-slate-100 px-2 py-2 text-left text-xs uppercase tracking-wider cursor-pointer"
                                >
                                    CREATED DATE
                                    <svg
                                        v-show="sortBy === 'created_at'"
                                        :class="!sortAsc ? 'rotate-180' : ''"
                                        class="inline h-5 w-5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                            clip-rule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <div
                                    scope="col"
                                    class="min-w-1-2 lg:w-3/6 bg-slate-100 px-2 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
                                >
                                    TASK / NOTES
                                </div>
                                <div
                                    scope="col"
                                    class="min-w-1-3 lg:w-1/6 bg-slate-100 px-2 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
                                >
                                    ASSIGN
                                </div>
                                <div
                                    @click.prevent="
                                        sortBy = 'estimate_finish';
                                        sortAsc = !sortAsc;
                                    "
                                    :class="sortBy === 'estimate_finish' ? 'font-semibold text-blue-600' : 'font-medium text-slate-600'"
                                    scope="col"
                                    class="min-w-1-3 lg:w-1/6 bg-slate-100 px-2 py-2 text-left text-xs uppercase tracking-wider cursor-pointer"
                                >
                                    EST. FINISH
                                    <svg
                                        v-show="sortBy === 'estimate_finish'"
                                        :class="!sortAsc ? 'rotate-180' : ''"
                                        class="inline h-5 w-5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                            clip-rule="evenodd"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <transition-group name="slide-up">
                                <div v-for="work in filteredAssignee" :key="work.id"
                                     class="bg-white divide-y divide-slate-200 w-full hover:bg-slate-100">
                                    <div class="flex w-full border-b border-slate-200">
                                        <div
                                            class="priority-and-work-id px-2 lg:pl-6 py-2 min-w-1-3 lg:w-1/6 hover:bg-slate-100 whitespace-nowrap">
                                            <div class="flex items-center">
                                                <span
                                                    v-if="!work.storm_event && work.inspection && work.inspection.priority === 1"
                                                    title="Low Priority"
                                                    class="h-3 w-3 rounded-full ring-2 ring-white bg-green-500"
                                                ></span>
                                                <span
                                                    v-if="!work.storm_event && work.inspection && work.inspection.priority === 2"
                                                    title="Medium Priority"
                                                    class="h-3 w-3 rounded-full ring-2 ring-white bg-orange-400"
                                                ></span>
                                                <span
                                                    v-if="!work.storm_event && work.inspection && work.inspection.priority === 3"
                                                    title="High Priority"
                                                    class="h-3 w-3 rounded-full ring-2 ring-white bg-red-600"
                                                ></span>
                                                <img
                                                    v-if="work.inspection && work.storm_event"
                                                    :src="'/images/icons/storm-' + work.inspection.priority + '.png'"
                                                    class="-ml-1 w-5 h-5"
                                                    title="Storm Event"
                                                />
                                                <div class="ml-2">
                                                    <div class="text-sm font-medium text-slate-800 pt-1">
                                                        <div class="text-xs text-slate-500 font-normal uppercase"
                                                             style="line-height: 0.75">Works ID
                                                        </div>
                                                        <a :href="'/maintenance-map?treeId=' + work.tree.id"
                                                           class="hover:text-blue-700">
                                                            {{ work.id }}
                                                        </a>
                                                    </div>
                                                    <!-- <div class="text-xs text-slate-500">
                                                        {{ work.city }}
                                                    </div> -->
                                                </div>
                                            </div>
                                            <div class="flex items-center">
                                                <span
                                                    v-if="work.trees_risk_id === 0"
                                                    class="h-3 w-3 rounded-full border-2 border-slate-300"
                                                    title="Risk Not Assessed"
                                                ></span>
                                                <span
                                                    v-if="work.trees_risk_id === 1"
                                                    class="h-3 w-3 rounded-full border-2 border-green-500"
                                                    title="Low Risk Tree"
                                                ></span>
                                                <span
                                                    v-if="work.trees_risk_id === 2"
                                                    class="h-3 w-3 rounded-full border-2 border-orange-400"
                                                    title="Moderate Risk Tree"
                                                ></span>
                                                <span
                                                    v-if="work.trees_risk_id === 3"
                                                    class="h-3 w-3 rounded-full border-2 border-red-600"
                                                    title="High Risk Tree"
                                                ></span>
                                                <span
                                                    v-if="work.trees_risk_id === 4"
                                                    class="h-3 w-3 rounded-full border-2 border-red-800"
                                                    title="Extreme Risk Tree"
                                                ></span>
                                                <div class="ml-2">
                                                    <div class="text-sm font-medium text-slate-800 pt-3">
                                                        <div class="text-xs text-slate-500 font-normal uppercase"
                                                             style="line-height: 0.75">Tree ID
                                                        </div>
                                                        <a :href="'/maintenance-map?treeId=' + work.tree.id"
                                                           class="hover:text-blue-700">
                                                            {{ work.tree.id }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            class="last-updated px-2 py-2 min-w-1-3 lg:w-1/6 hover:bg-slate-100 whitespace-nowrap text-xs text-slate-500">
                                            <div class="text-sm font-medium text-slate-600">
                                                {{ forestreeHelpers.dateTime(work.created_at) }}
                                            </div>
                                            <div v-if="work.inspection" class="text-xs text-slate-500">
                                                {{ work.inspection.name }}
                                            </div>
                                            <div v-if="work.created_at !== work.updated_at"
                                                 class="text-xs text-slate-500 pt-2">
                                                Last Updated:<br><span class="text-slate-600">{{
                                                    forestreeHelpers.dateTime(work.updated_at)
                                                }}</span>
                                            </div>
                                        </div>
                                        <div class="tasks-or-notes px-2 py-2 min-w-1-2 lg:w-3/6 whitespace-nowrap">
                                            <div>
                                                <div v-if="work.address"
                                                     class="font-semibold pb-1 text-slate-600 text-xs">
                                                    {{ work.address }}
                                                </div>

                                                <span
                                                    v-if="work.work_orders"
                                                    @click="showWO(work.work_orders.wo_url_link)"
                                                    class="inline-block cursor-pointer bg-orange-500 rounded-full px-3 text-sm text-orange-100 hover:text-orange-300 mr-1"
                                                >
                                                    {{ work.work_orders.woWorkOrderNumber }}
                                                </span>
                                                <span
                                                    v-if="work.crm"
                                                    class="inline-block bg-red-500 rounded-full px-3 text-sm text-red-100 hover:text-red-300 mr-1"
                                                >
                                                    {{ work.crm.crm_id }}
                                                </span>
                                                <span
                                                    v-for="e in work.equipment"
                                                    :key="e.id"
                                                    @click="toggleEquip(e.name)"
                                                    class="inline-block bg-blue-500 rounded-full px-3 text-sm text-blue-100 hover:text-blue-200 cursor-pointer mr-1"
                                                >
                                                    {{ e.name }}
                                                </span>
                                                <div v-if="work.inspection" class="text-sm text-slate-500 mt-1">
                                                    <span
                                                        v-for="(task, tidx) in work.inspection.works_as_list"
                                                        :key="tidx"
                                                        @click="toggleTasks(task)"
                                                        class="inline-block bg-slate-500 rounded-full px-3 text-sm text-slate-100 hover:text-slate-300 cursor-pointer mr-1 mb-1"
                                                    >
                                                        {{
                                                            forestreeHelpers.getWorkTask(task, props.tasks)
                                                        }}
                                                    </span>
                                                </div>
                                                <div v-if="work.inspection" class="text-wrap">
                                                    <span v-if="work.inspection.work_notes"
                                                          class="inline-block rounded-full text-xs text-slate-500">
                                                        {{ work.inspection.work_notes }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- <div v-else class="px-2 py-2 min-w-1-3 lg:w-1/6 hover:bg-slate-100 whitespace-nowrap">
                                            <div>
                                                <div class="text-sm text-slate-900">There are {{tree.works.length}} open works for this tree</div>
                                            </div>
                                        </div> -->
                                        <div
                                            class="assign-work-section min-w-1-3 lg:w-1/6 hover:bg-slate-100 whitespace-nowrap px-2">
                                            <div v-if="users && users.active_users.length > 0" class="">
                                                <assign-work class="" :work="work" :users="users" :teams="teams"
                                                             assignTypeId="2" :permissions="permissions"/>
                                            </div>
                                            <div v-if="work.projects.length > 0" class="px-2">
                                                <div v-for="e in work.projects" :key="e.id">
                                                    <popper
                                                        trigger="click"
                                                        :append-to-body="true"
                                                        :options="{
                                                            placement: 'top',
                                                            modifiers: {
                                                                offset: {
                                                                    offset: '0,0',
                                                                },
                                                            },
                                                        }"
                                                    >
                                                        <template #content="{ close }">
                                                            <div class="popper">
                                                                <div class="">
                                                                    <div class="text-slate-600 px-4 pt-4">
                                                                        <div
                                                                            class="text-lg font-semibold text-orange-600 pb-2">
                                                                            {{ e.name }}
                                                                        </div>
                                                                        <div
                                                                            class="font-semibold text-xs text-slate-500 pb-0">
                                                                            Total Trees/Completed
                                                                        </div>
                                                                        <div
                                                                            class="text-lg font-semibold text-slate-700 pb-2">
                                                                            {{ e.total_trees }}/{{ e.total_complete }}
                                                                        </div>
                                                                    </div>
                                                                    <div class="flex w-full text-sm">
                                                                        <div class="w-1/2 p-2 flex justify-center">
                                                                            <a
                                                                                :href="'/maintenance-map?projectId=' + e.url_link"
                                                                                target="_blank"
                                                                                class="w-full text-center font-semibold text-slate-200 hover:text-slate-100 bg-slate-700 py-2 hover:bg-slate-600 rounded-lg cursor-pointer"
                                                                            >
                                                                                View Project
                                                                            </a>
                                                                        </div>
                                                                        <div class="w-1/2 p-2 flex justify-center">
                                                                            <a
                                                                                :href="'/projects?goto=' + e.id"
                                                                                class="w-full text-center font-semibold text-blue-700 hover:text-blue-500 bg-slate-100 py-2 hover:bg-slate-200 rounded-lg cursor-pointer"
                                                                            >
                                                                                Projects Page
                                                                            </a>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </template>
                                                        <span
                                                            class="inline-block text-center bg-orange-500 rounded-full px-2 text-sm text-orange-100 hover:text-orange-200 cursor-pointer mr-1"
                                                        >
                                                            {{
                                                                e.name.length < 10 ? e.name : e.name.substring(0, 10) + "..."
                                                            }}
                                                        </span>
                                                    </popper>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            v-if="work.estimate_finish"
                                            class="px-2 py-2 min-w-1-3 lg:w-1/6 hover:bg-slate-100 whitespace-nowrap text-xs text-slate-500"
                                        >
                                            <div class="text-sm font-medium text-slate-600">
                                                {{ forestreeHelpers.justDate(work.estimate_finish) }}
                                            </div>
                                            <div class="text-xs text-slate-500">
                                                {{ forestreeHelpers.daysTo(work.estimate_finish) }}
                                            </div>
                                            <div v-if="isOverdue(work.estimate_finish)" class="pt-1">
                                                <span
                                                    class="text-xs rounded-full px-2 py-1 bg-red-200 text-red-700 font-semibold">Overdue</span>
                                            </div>
                                        </div>
                                        <div v-else
                                             class="px-2 py-2 min-w-1-3 lg:w-1/6 hover:bg-slate-100 whitespace-nowrap">
                                            <div class="text-xs text-slate-400">NA</div>
                                        </div>
                                    </div>
                                </div>
                            </transition-group>

                            <div v-if="showLimit < filteredLength" class="w-full flex justify-center py-3">
                                <button @click="showLimit = showLimit + 50"
                                        class="py-2 px-5 bg-slate-200 text-slate-700 font-semibold rounded-lg">
                                    Show More
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <transition name="slide-out">
            <div
                v-if="showWorkOrder"
                class="fixed inset-0 overflow-hidden"
                aria-labelledby="slide-over-title"
                role="dialog"
                aria-modal="true"
                style="z-index: 100; background-color: #607d8b4d"
            >
                <div class="absolute inset-0 overflow-hidden">
                    <div class="absolute inset-0" aria-hidden="true"></div>

                    <div class="fixed inset-y-0 right-0 pl-10 w-full lg:w-1/2 flex sm:pl-16">
                        <div class="w-full">
                            <div class="h-full flex flex-col pt-6 bg-white shadow-xl overflow-auto">
                                <div class="px-4 sm:px-6">
                                    <div class="flex items-start justify-between">
                                        <h2 class="text-lg font-medium text-slate-900" id="slide-over-title">
                                            TechnologyOne Work Order</h2>
                                        <div class="ml-3 h-7 flex items-center">
                                            <button
                                                @click="showWorkOrder = false"
                                                class="bg-white rounded-md text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            >
                                                <!-- Heroicon name: outline/x -->
                                                <svg
                                                    class="h-6 w-6"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    aria-hidden="true"
                                                >
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                          stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="selectedWorkOrderURL" class="mt-6 relative flex-1">
                                    <iframe class="w-full h-full" :src="selectedWorkOrderURL"></iframe>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <bulk-assign
            v-if="showBulkAssign"
            :selectedFeatures="selectedFeatures"
            :users="users"
            :teams="teams"
            :permissions="permissions"
            :set-bulk-assign="setBulkAssign"
            :bulk-updated="bulkUpdated"
        />

        <works-project
            v-if="showWorksProject"
            :polygonWkt="polygonWkt"
            :selectedFeatures="selectedFeatures"
            :users="users"
            :permissions="permissions"
            @update:showWorksProject="showWorksProject = false"
        />

        <bulk-complete-works
            v-if="showBulkComplete"
            :selectedFeatures="selectedFeatures"
            :users="users"
            :permissions="permissions"
            :show-bulk-complete="showBulkComplete"
            :bulk-updated="bulkUpdated"
            @update:showBulkComplete="showBulkComplete = false"
        />
    </div>
</template>

<style>
/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-out-enter-active {
    opacity: 1;
    transition: all 0.3s ease;
}

.slide-out-leave-active {
    opacity: 1;
    transition: all 0.3s ease;
}

.slide-out-enter,
.slide-out-leave-to {
    transform: translateX(50vw);
    opacity: 0;
}

.slide-up-enter-active {
    opacity: 1;
    transition: all 0.2s ease;
}

.slide-up-leave-active {
    opacity: 1;
    transition: all 0.2s ease;
}

.slide-up-enter,
.slide-up-leave-to {
    transform: translateY(-50px);
    opacity: 0;
}

.rotate-180 {
    transform: rotate(180deg);
}

.style-chooser .vs__selected {
    background-color: #edf2f7;
    border: 1px solid #dbebfb;
    color: #2b6cb0;
    line-height: 1.7;
}

.style-chooser .vs__search {
    line-height: 1.7;
}

.map-height {
    height: 70vh;
}

.style-chooser .vs__search::placeholder,
.style-chooser .vs__dropdown-toggle,
.style-chooser .vs__dropdown-menu {
    background: #ffffff;
    border: 1px solid #cbd5e0;
    color: #394066;
}

.style-chooser .vs__clear,
.style-chooser .vs__open-indicator {
    fill: #394066;
}

.min-w-1-2 {
    min-width: 50%;
}

.min-w-1-3 {
    min-width: 33.33%;
}

@media (min-width: 1024px) {
    .min-w-1-3 {
        min-width: auto;
    }
}

.popper {
    min-width: 250px;
    max-width: 350px;
    text-align: left;
    padding: 0px;
    font-size: 15px;
    box-shadow: rgb(58 58 58 / 50%) 3px 3px 10px 1px;
}
</style>
